// Role sheets (tblNode Type = 'SHEET'). SheetJSON holds the seats model:
//   { v: 2,
//     sections: [ { id, label, hint? } ],           shared by every seat, in order; hint from a role sheet template
//     seats: [ { id,                                 tblEmployee.RoleSheetColID and ?col= links point at this
//                name, title,                        "Jason", "CEO" (either can be empty)
//                accountabilities: [ { value, value2 } ],
//                values: { [sectionId]: html } } ] }
// SheetColsJSON mirrors the seats as [ { id, accountabilities } ].
//
// A seat sign-off stores a snapshot of the seat it signed (tblNodeSignOff.CellsJSON):
//   { v: 2, seatId, name, title, accountabilities: [ { value, value2 } ], sections: [ { label, html } ] }   (filled sections only)
import { v4 as uuidv4 } from 'uuid';

export function plainText(html: any): string {
    return `${html || ''}`.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/\s+/g, ' ').trim();
}

// Section names match ignoring case, punctuation, a trailing colon and plurals ("look like" = "looks like")
export function labelKey(label: string): string {
    return plainText(label).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
        .split(' ').map(word => word.replace(/s$/, '')).join(' ');
}

export function isSeatsModel(sheet: any): boolean {
    return !!sheet && sheet.v == 2 && Array.isArray(sheet.seats) && Array.isArray(sheet.sections);
}

// A node's role sheet (an empty one when SheetJSON is missing or not the seats model)
export function readRoleSheet(node: any): any {
    let sheet: any = null;
    try { sheet = JSON.parse(node.SheetJSON); } catch(e) { sheet = null; }
    if(!isSeatsModel(sheet)) return { v: 2, sections: [], seats: [] };
    for(const seat of sheet.seats) {
        if(!Array.isArray(seat.accountabilities)) seat.accountabilities = [];
        if(!seat.values) seat.values = {};
    }
    return sheet;
}

// The sections most role sheets use, for new sheets when the company has no default role sheet template
export const DEFAULT_SECTIONS = [ 'Your Main Purpose', 'Direct Reports', 'Weekly Metrics You Watch', 'Key Responsibilities', 'Decision-Making Authority', 'Recurring Meetings', 'What Success Looks Like' ];

// A new sheet with one empty seat. sections: a role sheet template's [{ label, hint }] (API rolesheettemplates.ts),
// else DEFAULT_SECTIONS. The hints are copied onto the sheet, so the editor shows them however the template changes.
export function newRoleSheet(sections?: { label: string, hint?: string }[]): any {
    const list: { label: string, hint?: string }[] = sections && sections.length ? sections : DEFAULT_SECTIONS.map(label => ({ label }));
    return { v: 2, sections: list.map(s => newSection(s.label, s.hint)), seats: [ newSeat() ] };
}

export function newSeat(name = '', title = ''): any {
    return { id: uuidv4(), name, title, accountabilities: [], values: {} };
}

// hint: what belongs in the section, shown under it in the editor (EditSheet.vue); from a role sheet template
export function newSection(label: string, hint = ''): any {
    return hint ? { id: uuidv4(), label, hint } : { id: uuidv4(), label };
}

// A seat's (or seat sign-off snapshot's) name and title, trimmed ('' when missing)
export function seatNameTitle(seat: any): { name: string, title: string } {
    return { name: `${(seat && seat.name) || ''}`.trim(), title: `${(seat && seat.title) || ''}`.trim() };
}

// "Jason - CEO", or whichever of the two it has ('' when neither)
export function seatLabel(seat: any): string {
    const { name, title } = seatNameTitle(seat);
    return [ name, title ].filter(Boolean).join(' - ');
}

// Initials of the seat's name, else its title
export function seatInitials(seat: any): string {
    const { name, title } = seatNameTitle(seat);
    const letters = (name || title).split(/\s+/).filter(Boolean).map(w => w[0]).join('');
    return (letters.slice(0, 2) || '?').toUpperCase();
}

// The seat an employee's role points at (tblEmployee.RoleSheetSlug + RoleSheetColID), from the store's nodes
export function findSeat(nodes: any[], slug: string, seatId: string): { node: any, sheet: any, seat: any } | null {
    if(!slug || !seatId) return null;
    const node = (nodes || []).find((n: any) => n.Slug == slug);
    if(!node) return null;
    const sheet = readRoleSheet(node);
    const seat = sheet.seats.find((s: any) => s.id == seatId);
    return seat ? { node, sheet, seat } : null;
}

// The title an employee's seat gives them, to show beside their name (e.g. accountability chart cards):
// the seat's title, else the role sheet's name. '' when they have no seat.
export function seatTitle(nodes: any[], slug: string, seatId: string): string {
    const found = findSeat(nodes, slug, seatId);
    if(!found) return '';
    return found.seat.title || found.node.Name || '';
}

// A seat's filled sections in sheet order, as [{ id, label, html }] (what RoleSeat shows)
export function seatSections(sheet: any, seat: any): any[] {
    return ((sheet && sheet.sections) || [])
        .map((section: any) => ({ id: section.id, label: section.label || '', html: (seat.values || {})[section.id] || '' }))
        .filter((section: any) => plainText(section.html));
}

export function seatAccountabilities(seat: any): any[] {
    return ((seat && seat.accountabilities) || []).filter((a: any) => a && (a.value || a.value2));
}

// What a seat sign-off saves: the seat as it read when it was signed
export function seatSnapshot(sheet: any, seatId: string): any | null {
    const seat = ((sheet && sheet.seats) || []).find((s: any) => s.id == seatId);
    if(!seat) return null;
    return {
        v: 2,
        seatId: seat.id,
        ...seatNameTitle(seat),
        accountabilities: seatAccountabilities(seat).map((a: any) => ({ value: a.value || '', value2: a.value2 || '' })),
        sections: (sheet.sections || [])
            .filter((section: any) => plainText((seat.values || {})[section.id]))
            .map((section: any) => ({ label: section.label || '', html: seat.values[section.id] }))
    };
}

// A sign-off's seat snapshot, or null when it isn't a seat sign-off
export function readSeatSignOff(signOff: any): any | null {
    if(!signOff || !signOff.CellsJSON) return null;
    try {
        const snapshot = JSON.parse(signOff.CellsJSON);
        return snapshot && snapshot.v == 2 && snapshot.seatId ? snapshot : null;
    } catch(e) {
        return null;
    }
}
